// Raw px, fed to each column's `width` and summed into the table's scroll width. Sized so two-line
// headers don't collide; the total exceeds a laptop viewport by design and the table scrolls.
export const DETAIL_COLUMN_WIDTHS = {
  year: 88,
  crop: 190,
  yield: 88,
  sellingPrice: 88,
  strawRevenue: 92,
  revenueTotal: 88,
  lossesFodder: 88,
  lossesMould: 88,
  revenueAdjusted: 92,
  subsidies: 88,
  seedMain: 104,
  seedCover: 104,
  fertiliser: 88,
  plantProtection: 88,
  diesel: 88,
  machinery: 92,
  irrigation: 92,
  other: 88,
  cleaning: 92,
  dryingStorage: 100,
  insurance: 92,
  totalCosts: 88,
  yieldChanges: 88,
  costSavings: 88,
  resilienceImpact: 96,
  cmMid: 84,
  cmRange: 116,
  cumulativeCm: 88,
} as const;

const sumWidths = (keys: ReadonlyArray<keyof typeof DETAIL_COLUMN_WIDTHS>): number => keys.reduce((total, key) => total + DETAIL_COLUMN_WIDTHS[key], 0);

const SHARED_KEYS = [
  "year",
  "crop",
  "yield",
  "sellingPrice",
  "strawRevenue",
  "revenueTotal",
  "lossesFodder",
  "lossesMould",
  "revenueAdjusted",
  "subsidies",
  "seedMain",
  "seedCover",
  "fertiliser",
  "plantProtection",
  "diesel",
  "machinery",
  "totalCosts",
  "yieldChanges",
  "costSavings",
  "resilienceImpact",
  "cmMid",
  "cmRange",
  "cumulativeCm",
] as const;

/** Table min-width with "Other" collapsed (single column) and expanded (three sub-columns). */
export const DETAIL_TABLE_MIN_WIDTH_COLLAPSED = sumWidths([...SHARED_KEYS, "other"]);
export const DETAIL_TABLE_MIN_WIDTH_EXPANDED = sumWidths([...SHARED_KEYS, "cleaning", "dryingStorage", "insurance"]);

/** Edit-view column widths (px). One column per editable field, plus the sticky crop column. */
export const EDIT_COLUMN_WIDTHS = {
  crop: 190,
  input: 128,
} as const;

/** Crop column plus one input column per editable field; pass EDITABLE_FIELDS.length. */
export const editTableMinWidth = (fieldCount: number): number => EDIT_COLUMN_WIDTHS.crop + EDIT_COLUMN_WIDTHS.input * fieldCount;

/** Compare-view column widths (px). Non-baseline scenarios add a delta column. */
export const COMPARE_COLUMN_WIDTHS = {
  // Wide enough for the "Total ({{count}} years)" summary label on one line, which sits in this column alone.
  year: 144,
  crop: 180,
  cmMid: 96,
  cumulativeCm: 96,
  delta: 104,
} as const;

export const COMPARE_SCENARIO_GROUP_WIDTH_BASELINE = COMPARE_COLUMN_WIDTHS.crop + COMPARE_COLUMN_WIDTHS.cmMid + COMPARE_COLUMN_WIDTHS.cumulativeCm;
export const COMPARE_SCENARIO_GROUP_WIDTH_COMPARISON = COMPARE_SCENARIO_GROUP_WIDTH_BASELINE + COMPARE_COLUMN_WIDTHS.delta;

/** Compare body height in the Detailed calculations tab; antd pins the header row above it. */
export const COMPARE_TABLE_SCROLL_Y = "60vh";

/** Above antd drawers (1002) so the discard confirmation is never hidden behind one. */
export const DISCARD_CONFIRM_Z_INDEX = 1010;

export const DISCARD_CONFIRM_WIDTH = 440;

// Only fields collected elsewhere in the scenario view; calculated cost lines stay read-only.
// Units are the ones that persist, in the farm's currency: seedCosts per kg, minFertiliser per kg N, sellingPrice per t.
// Numeric limits live in CROP_YEAR_FIELD_LIMITS, shared with CropDetailsInputDrawer.
export const EDITABLE_FIELDS = [
  { key: "yield", labelKey: "yield", unit: "t/ha" },
  { key: "sellingPrice", labelKey: "sellingPrice", unit: "{{currency}}/t" },
  { key: "fodderLosses", labelKey: "lossesFodder", unit: "%" },
  { key: "mouldLosses", labelKey: "lossesMould", unit: "%" },
  { key: "seedCosts", labelKey: "seedPrice", unit: "{{currency}}/kg" },
  { key: "seedAmount", labelKey: "seedAmount", unit: "kg/ha" },
  { key: "minFertiliser", labelKey: "fertiliserPrice", unit: "{{currency}}/kg N" },
  { key: "plantProtection", labelKey: "plantProtection", unit: "{{currency}}/ha" },
  // Only editable on a year that grows a cover crop; the calculation ignores it otherwise.
  { key: "ccCost", labelKey: "coverCropSeedPrice", unit: "{{currency}}/ha", coverCropOnly: true },
  { key: "legumeShare", labelKey: "legumeShare", unit: "%", coverCropOnly: true },
] as const;

export type EditableFieldKey = (typeof EDITABLE_FIELDS)[number]["key"];
